import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import { orchard } from '../../theme';
import useHoldToTalk from './useHoldToTalk';
import { canRecord } from './recorder';

/* Hold to dictate: the recognised text goes to onText(text). A quick tap keeps
   listening until the next tap. The ring breathes with the voice. */
export default function MicButton({
  onText,
  onError,
  en,
  size = 40,
  disabled,
}) {
  const talk = useHoldToTalk({ onText, onError });
  const ringRef = useRef(null);
  const { state, level } = talk;
  const recording = state === 'recording' || state === 'starting';

  useEffect(() => {
    if (!recording) return undefined;
    let raf = 0;
    let smooth = 0;
    const tick = () => {
      smooth += (level() - smooth) * 0.35;
      if (ringRef.current) {
        ringRef.current.style.transform = `scale(${1 + smooth * 0.55})`;
        ringRef.current.style.opacity = String(0.25 + smooth * 0.6);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [recording, level]);

  if (!canRecord()) return null;

  const title =
    state === 'transcribing'
      ? en
        ? 'Writing it down…'
        : 'Записываю…'
      : talk.latched
        ? en
          ? 'Listening — tap to finish'
          : 'Слушаю — нажмите, чтобы закончить'
        : en
          ? 'Hold and speak (a tap: talk hands-free). On a PC: hold right Ctrl anywhere'
          : 'Удерживайте и говорите (короткое нажатие — без удержания). На ПК: правый Ctrl в любом поле';

  return (
    <Tooltip title={title}>
      <Box
        sx={{ position: 'relative', flexShrink: 0, width: size, height: size }}
      >
        <Box
          ref={ringRef}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            bgcolor: alpha(orchard.cherry, 0.5),
            opacity: 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
          }}
        />
        <IconButton
          disabled={disabled || state === 'transcribing'}
          aria-label={en ? 'Dictate' : 'Надиктовать'}
          onPointerDown={(e) => {
            if (e.button !== undefined && e.button !== 0) return;
            e.preventDefault();
            e.currentTarget.setPointerCapture?.(e.pointerId);
            talk.press();
          }}
          onPointerUp={() => talk.release()}
          onPointerCancel={() => talk.release()}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
              e.preventDefault();
              talk.press();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter' || e.key === ' ') talk.release();
          }}
          sx={{
            position: 'relative',
            width: size,
            height: size,
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            color: recording ? '#fff' : 'text.secondary',
            bgcolor: recording ? orchard.cherry : 'transparent',
            transition: 'background-color 0.2s ease, color 0.2s ease',
            '&:hover': { bgcolor: recording ? orchard.cherry : undefined },
          }}
        >
          {state === 'transcribing' ? (
            <CircularProgress size={20} />
          ) : (
            <MicRoundedIcon />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
}
